import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "../lib/LanguageContext";

// A sub-pixel remainder (zoom, fractional widths) is not hidden content.
const EDGE_SLACK = 1;
// One press reveals most of a screen of columns while keeping a column of context.
const STEP_RATIO = 0.8;

/**
 * Horizontal scroller for a super-admin ledger table that may be wider than its box.
 *
 * Scrolling alone is not enough: on a 25-row page the scrollbar sits at the table's bottom,
 * below the fold, so columns past the right edge were invisible with nothing saying they
 * exist (browser QA round 1, Transactions at 1440). Each side that hides content gets a
 * cue: a fade over the edge and a named button that scrolls a screen of columns into view
 * (a mouse without a horizontal wheel has no other way there). The cues follow the real
 * geometry: re-measured on scroll, after every render (a new page can change the table's
 * width), and on resize of the box or the table.
 *
 * `testId` names the scroller; the cue buttons are `<testId>-more-start` / `-more-end`, and
 * the scroller carries `data-overflow-start` / `data-overflow-end` for browser checks.
 * The frame is `relative` so the cues overlay the scroller only, never a pager below it.
 */
export default function HorizontalScroller({ testId, children }) {
  const t = useT("superadmin");
  const ref = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const hidden = el.scrollWidth - el.clientWidth;
    const next = { start: el.scrollLeft > EDGE_SLACK, end: el.scrollLeft < hidden - EDGE_SLACK };
    setEdges((cur) => (cur.start === next.start && cur.end === next.end ? cur : next));
  }, []);

  // After every render: cheap, and `measure` only sets state when an edge flips.
  useLayoutEffect(measure);

  useLayoutEffect(() => {
    const el = ref.current;
    // jsdom has no ResizeObserver; there is no layout to follow there either.
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [measure]);

  const step = (direction) => {
    const el = ref.current;
    el.scrollBy({ left: direction * el.clientWidth * STEP_RATIO, behavior: "smooth" });
  };

  const cue = (side) => {
    const end = side === "end";
    const Icon = end ? ChevronRight : ChevronLeft;
    return (
      <div
        className={`pointer-events-none absolute inset-y-0 ${end ? "right-0 bg-gradient-to-l" : "left-0 bg-gradient-to-r"} w-12 from-white to-transparent`}
      >
        <button
          type="button"
          onClick={() => step(end ? 1 : -1)}
          className={`pointer-events-auto absolute top-2.5 ${end ? "right-2" : "left-2"} flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40`}
          aria-label={t(end ? "sal.scrollEnd" : "sal.scrollStart")}
          title={t(end ? "sal.scrollEnd" : "sal.scrollStart")}
          data-testid={`${testId}-more-${side}`}
        >
          <Icon className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={measure}
        className="overflow-x-auto"
        data-testid={testId}
        data-overflow-start={String(edges.start)}
        data-overflow-end={String(edges.end)}
      >
        {children}
      </div>
      {edges.start && cue("start")}
      {edges.end && cue("end")}
    </div>
  );
}
